/**
 * ReloadIndicator - Circular progress overlay showing weapon reload duration with cancel support.
 */

import { BaseUIComponent } from './BaseUIComponent.js';

import type { BaseUIComponentOptions, UIComponentMetadata } from './BaseUIComponent.js';
import type * as ENGINE from '../../index.js';


export interface ReloadIndicatorOptions extends BaseUIComponentOptions {
  reloadDuration?: number;
  text?: string;
  color?: string;
}

export class ReloadIndicator extends BaseUIComponent<ReloadIndicatorOptions> {
  public static metadata: UIComponentMetadata = {
    displayName: 'Reload Indicator',
    category: 'feedback',
    summary: 'Circular progress overlay showing reload duration with cancel support.',
    useCases: ['reload', 'cooldown', 'cast', 'channel', 'progress overlay'],
    optionsType: 'ReloadIndicatorOptions',
    assetPaths: {
      template: '@engine/assets/ui/templates/reload-indicator.html',
      styles: '@engine/assets/ui/styles/reload-indicator.css',
    },
  };

  private reloadText: ENGINE.UIElement | null = null;
  private reloadProgress: ENGINE.UIElement | null = null;

  private isReloading = false;
  private animationId: number | null = null;

  protected getAssetPaths(): { templatePath: string; stylesPath: string } {
    return {
      templatePath: ReloadIndicator.metadata.assetPaths.template,
      stylesPath: ReloadIndicator.metadata.assetPaths.styles,
    };
  }

  protected getDefaultOptions(): Required<ReloadIndicatorOptions> {
    return {
      position: 'center',
      visible: false, // Hidden by default
      customClasses: [],
      customStyles: {},
      reloadDuration: 2000,
      text: 'RELOADING',
      color: '#4CAF50'
    };
  }

  protected getInitialData(): Record<string, any> {
    return {
      text: this.options.text,
      color: this.options.color
    };
  }

  protected override cacheElements(): void {
    if (!this.layout) return;

    this.reloadText = this.createElementWrapper(this.layout.querySelector('.reload-text'));
    this.reloadProgress = this.createElementWrapper(this.layout.querySelector('.reload-progress-container'));

    this.elements.set('reloadText', this.reloadText);
    this.elements.set('reloadProgress', this.reloadProgress);
  }

  protected override async onInitialize(): Promise<void> {
    // Component starts hidden by default
    this.hideReloadElements();
  }

  // Public API methods
  public startReload(): Promise<void> {
    if (this.isReloading) {
      return Promise.resolve();
    }

    this.isReloading = true;
    this.showReloadElements();

    return new Promise((resolve) => {
      const startTime = Date.now();

      const updateProgress = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / this.options.reloadDuration, 1);

        // Update the circular progress bar
        const circumference = 314; // 2 * π * 50
        const offset = circumference * (1 - progress);

        const progressFill = this.reloadProgress?.element.querySelector('.reload-progress-fill') as SVGCircleElement;
        if (progressFill) {
          progressFill.style.strokeDashoffset = offset.toString();
        }

        if (progress < 1) {
          this.animationId = requestAnimationFrame(updateProgress);
        } else {
          // Reload complete
          this.isReloading = false;
          this.hideReloadElements();

          // Reset progress bar
          if (progressFill) {
            progressFill.style.strokeDashoffset = '314';
          }

          resolve();
        }
      };

      this.animationId = requestAnimationFrame(updateProgress);
    });
  }

  public cancelReload(): void {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    this.isReloading = false;
    this.hideReloadElements();

    // Reset progress bar
    const progressFill = this.reloadProgress?.element.querySelector('.reload-progress-fill') as SVGCircleElement;
    if (progressFill) {
      progressFill.style.strokeDashoffset = '314';
    }
  }

  public isActive(): boolean {
    return this.isReloading;
  }

  public setDuration(duration: number): void {
    // Update the options directly since we're using the base class pattern
    (this.options as any).reloadDuration = duration;
  }

  public setText(text: string): void {
    // Update the options and UI
    (this.options as any).text = text;
    this.reloadText?.setText(text);
    const progressText = this.reloadProgress?.element.querySelector('.reload-progress-text');
    if (progressText) {
      progressText.textContent = text;
    }
  }

  // Private methods
  private showReloadElements(): void {
    this.reloadText?.addClass('active');
    this.reloadProgress?.addClass('active');
  }

  private hideReloadElements(): void {
    this.reloadText?.removeClass('active');
    this.reloadProgress?.removeClass('active');
  }

  protected override onDestroy(): void {
    // Cancel any ongoing reload
    this.cancelReload();

    // Clean up element references
    this.reloadText = null;
    this.reloadProgress = null;
  }
}
